import { useMutation } from "@apollo/client";
import {
  Alert,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { getWasteStreamRegistrationSubmissionMethod } from "encamp-shared/src/submissions/wasteStreamRegistration";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { SubmissionMethod } from "generated-graphql/graphql";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { CREATE_WASTE_STREAM_REGISTRATION_SUBMISSIONS_MUTATION } from "./api";

const AUTO_SENTINEL = "__AUTO__";

export type RegistrationStream = {
  id: string;
  name: string;
  facilityName: string;
  state: string;
};

/**
 * The method to pre-select in the dropdown. When every selected stream resolves
 * to the same per-state method we default to that concrete method (so the user
 * sees exactly what will be filed); when a batch spans states with differing
 * methods there is no single right answer, so we fall back to "Auto (by state)".
 */
function defaultMethodForStreams(
  streams: RegistrationStream[]
): SubmissionMethod | typeof AUTO_SENTINEL {
  const distinct = new Set(streams.map((s) => getWasteStreamRegistrationSubmissionMethod(s.state)));
  if (distinct.size !== 1) {
    return AUTO_SENTINEL;
  }
  const [only] = [...distinct];
  return Object.values(SubmissionMethod).find((m) => String(m) === String(only)) ?? AUTO_SENTINEL;
}

type Props = {
  open: boolean;
  onClose: () => void;
  tenantId: string;
  streams: RegistrationStream[];
  onSuccess: () => void;
};

export function CreateWasteStreamRegistrationSubmissionsDialog({
  open,
  onClose,
  tenantId,
  streams,
  onSuccess,
}: Props) {
  const alerts = useAlerts();
  const navigate = useNavigate();
  // Seeded once on mount. Callers render this dialog only while open (it mounts
  // fresh per open), so the initializer always sees the current stream set —
  // no syncing effect needed.
  const [methodOverride, setMethodOverride] = useState<SubmissionMethod | typeof AUTO_SENTINEL>(
    () => defaultMethodForStreams(streams)
  );

  const [createSubmissions, { loading }] = useMutation(
    CREATE_WASTE_STREAM_REGISTRATION_SUBMISSIONS_MUTATION,
    {
      refetchQueries: ["TenantWasteStreams", "WasteStreams"],
      awaitRefetchQueries: true,
    }
  );

  const handleClose = () => {
    onClose();
  };

  const handleSubmit = async () => {
    try {
      const result = await createSubmissions({
        variables: {
          input: {
            wasteStreamIds: streams.map((s) => s.id),
            submissionMethod:
              methodOverride === AUTO_SENTINEL ? undefined : (methodOverride ?? undefined),
          },
        },
      });

      const data = result.data?.createWasteStreamRegistrationSubmissions;
      const createdIds = data?.createdSubmissionIds ?? [];
      const created = data?.created ?? 0;
      const skipped = data?.skipped ?? 0;
      const failed = data?.failed ?? 0;

      // Single clean create: take the user straight to the new submission.
      if (createdIds.length === 1 && failed === 0) {
        const submissionId = createdIds[0];
        alerts.success("Submission created.");
        onSuccess();
        handleClose();
        navigate(`/o/${tenantId}/waste/submissions/${submissionId}/edit`);
        return;
      }

      const parts: string[] = [];
      if (created > 0) {
        parts.push(`${created} submission${created !== 1 ? "s" : ""} created`);
      }
      if (skipped > 0) {
        parts.push(`${skipped} skipped (already have submissions)`);
      }
      if (failed > 0) {
        parts.push(`${failed} failed`);
      }

      // The resolver settles each stream independently, so a successful GraphQL
      // response can still carry failures. Surface those as an error rather than
      // a misleading success.
      if (failed > 0) {
        alerts.error(parts.join(", ") || "Some submissions failed to create");
      } else {
        alerts.success(parts.join(", ") || "Done");
      }

      onSuccess();
      handleClose();
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      alerts.error(`Failed to create submissions: ${message}`, e instanceof Error ? e : undefined);
    }
  };

  const resolvedMethod = (stream: RegistrationStream) => {
    if (methodOverride !== AUTO_SENTINEL) {
      return prettyPrintEnumValue(methodOverride);
    }
    return prettyPrintEnumValue(getWasteStreamRegistrationSubmissionMethod(stream.state));
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      title="Create Registration Submissions"
      description={`Create a Waste Stream Registration submission for ${streams.length} waste stream${streams.length !== 1 ? "s" : ""}.`}
      primaryActionButtonProps={{
        onClick: handleSubmit,
        loading,
        loadingText: "Creating...",
        saveText: `Create ${streams.length} Submission${streams.length !== 1 ? "s" : ""}`,
        hideStartIcon: true,
        disabled: loading || streams.length === 0,
      }}
      cancelButtonProps={{ disabled: loading }}
    >
      <Alert severity="info" variant="outlined">
        Due date is set automatically to the earliest shipment date for each stream plus 30 days.
      </Alert>

      <FormControl size="small" sx={{ width: 280 }}>
        <InputLabel>Submission Method</InputLabel>
        <Select<SubmissionMethod | typeof AUTO_SENTINEL>
          value={methodOverride}
          label="Submission Method"
          onChange={(e: SelectChangeEvent<SubmissionMethod | typeof AUTO_SENTINEL>) =>
            setMethodOverride(e.target.value)
          }
        >
          <MenuItem value={AUTO_SENTINEL}>
            <em>Auto (by state)</em>
          </MenuItem>
          {Object.values(SubmissionMethod).map((value) => (
            <MenuItem key={value} value={value}>
              {prettyPrintEnumValue(value)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Streams ({streams.length})
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Waste Stream</TableCell>
              <TableCell>Facility</TableCell>
              <TableCell>State</TableCell>
              <TableCell>Method</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {streams.map((stream) => (
              <TableRow key={stream.id}>
                <TableCell>{stream.name}</TableCell>
                <TableCell>{stream.facilityName}</TableCell>
                <TableCell>{stream.state}</TableCell>
                <TableCell>{resolvedMethod(stream)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Dialog>
  );
}
